import { useState, useRef, useEffect } from 'react'
import {
  Image,
  FileText,
  Mic,
  Download,
  X,
  Upload,
  Loader2,
  RefreshCw,
} from 'lucide-react'
import {
  Dialog,
  DialogContent,
} from '@/components/ui/dialog'
import { supabase } from '@/lib/supabase'
import { useRetryTranscription } from '../hooks/use-upload-attachment'
import { useToast } from '@/hooks/use-toast'
import type { RecordAttachment } from '@repo/shared'

// ============================================================
// Display Mode (used in RecordCard)
// ============================================================

interface DisplayProps {
  attachments: RecordAttachment[]
  patientId: string
}

export function RecordAttachmentsDisplay({ attachments, patientId }: DisplayProps) {
  const [lightboxSrc, setLightboxSrc] = useState<string | null>(null)
  const retryTranscription = useRetryTranscription()
  const { toast } = useToast()

  if (attachments.length === 0) return null

  const images = attachments.filter((a) => a.file_type === 'image')
  const documents = attachments.filter((a) => a.file_type === 'document')
  const audios = attachments.filter((a) => a.file_type === 'audio')

  const getSignedUrl = async (filePath: string) => {
    const { data } = await supabase.storage
      .from('patient-records')
      .createSignedUrl(filePath, 3600) // 1 hour
    return data?.signedUrl
  }

  const handleImageClick = async (attachment: RecordAttachment) => {
    const url = await getSignedUrl(attachment.file_path)
    if (url) setLightboxSrc(url)
  }

  const handleDownload = async (attachment: RecordAttachment) => {
    const url = await getSignedUrl(attachment.file_path)
    if (url) {
      const link = document.createElement('a')
      link.href = url
      link.download = attachment.original_name
      link.click()
    }
  }

  const handleRetryTranscription = async (attachment: RecordAttachment) => {
    try {
      await retryTranscription.mutateAsync({
        attachmentId: attachment.id,
        filePath: attachment.file_path,
        patientId,
      })
      toast({
        title: 'Transcricao reiniciada',
        description: 'O audio esta sendo transcrito novamente.',
      })
    } catch {
      toast({
        title: 'Erro',
        description: 'Nao foi possivel reiniciar a transcricao.',
        variant: 'destructive',
      })
    }
  }

  return (
    <div className="space-y-2">
      {/* Images */}
      {images.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {images.map((img) => (
            <ImageThumbnail
              key={img.id}
              attachment={img}
              onClick={() => handleImageClick(img)}
            />
          ))}
        </div>
      )}

      {/* Documents */}
      {documents.length > 0 && (
        <div className="space-y-1">
          {documents.map((doc) => (
            <button
              key={doc.id}
              onClick={() => handleDownload(doc)}
              className="flex items-center gap-2 text-sm text-gray-600 hover:text-teal-600 transition-colors"
            >
              <FileText className="h-4 w-4 flex-shrink-0" />
              <span className="truncate">{doc.original_name}</span>
              <Download className="h-3.5 w-3.5 flex-shrink-0 opacity-50" />
            </button>
          ))}
        </div>
      )}

      {/* Audio */}
      {audios.length > 0 && (
        <div className="space-y-2">
          {audios.map((audio) => (
            <AudioAttachment
              key={audio.id}
              attachment={audio}
              onRetryTranscription={() => handleRetryTranscription(audio)}
            />
          ))}
        </div>
      )}

      {/* Lightbox */}
      <Dialog open={!!lightboxSrc} onOpenChange={() => setLightboxSrc(null)}>
        <DialogContent className="max-w-4xl p-0 overflow-hidden">
          {lightboxSrc && (
            <img
              src={lightboxSrc}
              alt="Anexo"
              className="w-full h-auto max-h-[80vh] object-contain"
            />
          )}
        </DialogContent>
      </Dialog>
    </div>
  )
}

function ImageThumbnail({
  attachment,
  onClick,
}: {
  attachment: RecordAttachment
  onClick: () => void
}) {
  const [src, setSrc] = useState<string | null>(null)

  // Lazy load signed URL
  useEffect(() => {
    supabase.storage
      .from('patient-records')
      .createSignedUrl(attachment.file_path, 3600)
      .then(({ data }) => {
        if (data?.signedUrl) setSrc(data.signedUrl)
      })
  }, [attachment.file_path])

  return (
    <button
      onClick={onClick}
      className="relative w-16 h-16 rounded-lg overflow-hidden bg-gray-100 border border-gray-200 hover:border-teal-300 transition-colors"
    >
      {src ? (
        <img src={src} alt={attachment.original_name} className="w-full h-full object-cover" />
      ) : (
        <div className="w-full h-full flex items-center justify-center">
          <Image className="h-5 w-5 text-gray-400" />
        </div>
      )}
    </button>
  )
}

function AudioAttachment({
  attachment,
  onRetryTranscription,
}: {
  attachment: RecordAttachment
  onRetryTranscription: () => void
}) {
  const [audioUrl, setAudioUrl] = useState<string | null>(null)

  // Load signed URL
  useEffect(() => {
    supabase.storage
      .from('patient-records')
      .createSignedUrl(attachment.file_path, 3600)
      .then(({ data }) => {
        if (data?.signedUrl) setAudioUrl(data.signedUrl)
      })
  }, [attachment.file_path])

  return (
    <div className="bg-gray-50 rounded-lg p-3 space-y-2">
      <div className="flex items-center gap-2">
        <Mic className="h-4 w-4 text-gray-500 flex-shrink-0" />
        <span className="text-sm text-gray-600 truncate">{attachment.original_name}</span>
      </div>

      {audioUrl && (
        <audio controls className="w-full h-8" preload="none">
          <source src={audioUrl} type={attachment.mime_type} />
        </audio>
      )}

      {/* Transcription */}
      {attachment.transcription_status === 'pending' && (
        <div className="flex items-center gap-2 text-xs text-gray-500">
          <Loader2 className="h-3.5 w-3.5 animate-spin" />
          Transcrevendo...
        </div>
      )}
      {attachment.transcription_status === 'completed' && attachment.transcription && (
        <p className="text-sm text-gray-600 bg-white rounded p-2 border border-gray-100">
          {attachment.transcription}
        </p>
      )}
      {attachment.transcription_status === 'failed' && (
        <button
          onClick={onRetryTranscription}
          className="flex items-center gap-1.5 text-xs text-amber-600 hover:text-amber-700"
        >
          <RefreshCw className="h-3.5 w-3.5" />
          Falha na transcricao - Tentar novamente
        </button>
      )}
    </div>
  )
}

// ============================================================
// Upload Mode (used in RecordForm)
// ============================================================

interface UploadProps {
  pendingFiles: PendingFile[]
  onAddFiles: (files: File[]) => void
  onRemoveFile: (index: number) => void
}

export interface PendingFile {
  file: File
  preview?: string
}

export function RecordAttachmentsUpload({
  pendingFiles,
  onAddFiles,
  onRemoveFile,
}: UploadProps) {
  const fileInputRef = useRef<HTMLInputElement>(null)
  const [dragActive, setDragActive] = useState(false)

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files ?? [])
    if (files.length > 0) onAddFiles(files)
    if (fileInputRef.current) fileInputRef.current.value = ''
  }

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault()
    setDragActive(false)
    const files = Array.from(e.dataTransfer.files)
    if (files.length > 0) onAddFiles(files)
  }

  return (
    <div className="space-y-3">
      {/* File drop zone */}
      <div
        onDragOver={(e) => { e.preventDefault(); setDragActive(true) }}
        onDragLeave={() => setDragActive(false)}
        onDrop={handleDrop}
        className={`border-2 border-dashed rounded-lg p-4 text-center transition-colors ${
          dragActive ? 'border-teal-400 bg-teal-50' : 'border-gray-200 hover:border-gray-300'
        }`}
      >
        <Upload className="h-5 w-5 text-gray-400 mx-auto mb-2" />
        <p className="text-sm text-gray-500">
          Arraste arquivos aqui ou{' '}
          <button
            type="button"
            onClick={() => fileInputRef.current?.click()}
            className="text-teal-600 hover:text-teal-700 underline"
          >
            selecione
          </button>
        </p>
        <p className="text-xs text-gray-400 mt-1">
          Imagens e documentos (max 50MB)
        </p>
        <input
          ref={fileInputRef}
          type="file"
          multiple
          accept="image/*,.pdf,.doc,.docx"
          onChange={handleFileSelect}
          className="hidden"
        />
      </div>

      {/* Pending files list */}
      {pendingFiles.length > 0 && (
        <div className="space-y-1.5">
          {pendingFiles.map((pf, index) => (
            <div
              key={index}
              className="flex items-center gap-2 bg-gray-50 rounded-lg px-3 py-2"
            >
              <FileIcon mimeType={pf.file.type} />
              <span className="text-sm text-gray-600 truncate flex-1">
                {pf.file.name}
              </span>
              <span className="text-xs text-gray-400 flex-shrink-0">
                {formatFileSize(pf.file.size)}
              </span>
              <button
                type="button"
                onClick={() => onRemoveFile(index)}
                className="p-1 text-gray-400 hover:text-red-500 transition-colors"
              >
                <X className="h-3.5 w-3.5" />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

function FileIcon({ mimeType }: { mimeType: string }) {
  if (mimeType.startsWith('image/')) return <Image className="h-4 w-4 text-teal-500 flex-shrink-0" />
  if (mimeType.startsWith('audio/')) return <Mic className="h-4 w-4 text-purple-500 flex-shrink-0" />
  return <FileText className="h-4 w-4 text-gray-500 flex-shrink-0" />
}

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}
